import type { SoundEffectRow } from '@/queries/soundEffects';
import {
  DEFAULT_SFX_VOLUME, isSfxLayer, MAX_CONCURRENT_SFX, MAX_SFX_LAYERS,
  peakSfxConcurrency, placeSoundEffect, SfxPlacementError, type SfxAlign,
} from '@/lib/ai-assistant/editor/soundEffects';
import { makeAudioLayer } from './layerTypeConfig';
import type { AudioLayer, MediaLayer } from './types';

export function assertSoundEffectConcurrency(layers: readonly MediaLayer[], layer: MediaLayer) {
  if (!isSfxLayer(layer) || peakSfxConcurrency(layers, layer) <= MAX_CONCURRENT_SFX) return;
  const others = layers
    .filter((other) => other.id !== layer.id && isSfxLayer(other) && other.start < layer.end && other.end > layer.start)
    .map((other) => other.id);
  throw new SfxPlacementError('too_many_overlapping_sfx', `At most ${MAX_CONCURRENT_SFX} sound effects at once; it overlaps ${others.join(', ')}`);
}

/** The manual picker and assistant create the same persisted audio layer. */
export function createSoundEffectLayer({
  id, sound, layers, at, clipDuration, align = 'start', end, volume = DEFAULT_SFX_VOLUME,
}: {
  id: string;
  sound: Pick<SoundEffectRow, 'id' | 'name' | 'url' | 'duration' | 'peak_at'>;
  layers: readonly MediaLayer[];
  at: number;
  clipDuration: number;
  align?: SfxAlign;
  end?: number;
  volume?: number;
}): AudioLayer {
  if (layers.filter(isSfxLayer).length >= MAX_SFX_LAYERS) {
    throw new SfxPlacementError('too_many_sfx', `A clip takes at most ${MAX_SFX_LAYERS} sound effects`);
  }
  const placed = placeSoundEffect({ at, align, peakAt: sound.peak_at, duration: sound.duration, clipDuration, end });
  const layer: AudioLayer = {
    ...makeAudioLayer({ id, name: sound.name, url: sound.url, start: placed.start, end: placed.end, mediaDuration: sound.duration }),
    uploading: false,
    volume,
    trimStart: placed.trimStart,
    trimEnd: placed.trimEnd,
    sfxId: sound.id,
  };
  assertSoundEffectConcurrency([...layers, layer], layer);
  return layer;
}
